// src/components/Hero/Hero.js
import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Card, Modal, Typography, Carousel } from 'antd';
import { 
  PlayCircleOutlined, 
  CheckCircleOutlined, 
  RocketOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import './Hero.css';

const { Title, Paragraph } = Typography;

const Hero = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Carousel data with images and corresponding text
  const carouselData = [
    {
      image: '/carousel1.jpg',
      title: 'Efficient Fleet Operations',
      description: 'Our modern fleet ensures timely deliveries with maximum efficiency and minimal environmental impact.'
    },
    {
      image: '/carousel2.jpg',
      title: 'Secure Cargo Handling',
      description: 'Advanced safety protocols protect your shipments throughout the entire transportation process.'
    },
    {
      image: '/carousel3.jpg',
      title: 'Round-the-Clock Service',
      description: '24/7 operations mean your logistics needs are met at any time, day or night.'
    },
    {
      image: '/hero.jpeg',
      title: 'Premium Delivery Solutions',
      description: 'Experience end-to-end delivery solutions that enhance your business operations.'
    }
  ];

  const settings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: true,
    beforeChange: (current, next) => setCurrentSlide(next),
    appendDots: dots => (
      <div>
        <ul className="custom-dots">{dots}</ul>
      </div>
    ),
    customPaging: i => (
      <div className="custom-dot">
        {i + 1}
      </div>
    )
  };

  return (
    <>
      <div className="hero-section">
        {/* Animated Background Elements */}
        <div className="hero-background">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
        </div>

        <div className="container">
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={12}>
              <div className="hero-content">
                {/* Badge */}
                <div className="hero-badge">
                  <span className="badge-text">Premium Delivery Solutions</span>
                </div>

                <Title className="hero-title">
                  <span className="title-line">Efficient Supply Chain</span>
                  <span className="title-line">
                    <span className="text-primary"> Excellence </span>
                    <span className="text-secondary"> Achieved</span>
                  </span>
                </Title>

                <Paragraph className="hero-description">
                  {carouselData[currentSlide].description}
                </Paragraph>

                <div className="hero-actions">
                  <Button 
                    type="primary" 
                    size="large" 
                    className="hero-btn-primary"
                    icon={<RocketOutlined />}
                  >
                    Explore Solutions
                    <ArrowRightOutlined className="btn-arrow" />
                  </Button>
                  <Button 
                    size="large" 
                    className="hero-btn-secondary"
                    icon={<PlayCircleOutlined />}
                    onClick={showModal}
                  >
                    Watch Success Story
                  </Button>
                </div>

                <div className="hero-features">
                  <div className="feature-item">
                    <div className="feature-icon-wrapper">
                      <CheckCircleOutlined className="feature-icon" />
                    </div>
                    <span className="feature-text">Advanced Shipment Monitoring</span>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon-wrapper">
                      <CheckCircleOutlined className="feature-icon" />
                    </div>
                    <span className="feature-text">Secure Handling</span>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon-wrapper">
                      <CheckCircleOutlined className="feature-icon" />
                    </div>
                    <span className="feature-text">24/7 Expert Support</span>
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="hero-visual">
                <Card className="hero-card">
                  <div className="card-content">
                    <Carousel {...settings} className="hero-carousel">
                      {carouselData.map((item, index) => (
                        <div key={index}>
                          <div className="carousel-slide">
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="carousel-image"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1601579532571-8d23dfc91ef8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
                              }}
                            />
                            <div className="carousel-overlay">
                              <h3>{item.title}</h3>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Carousel>
                    
                    {/* Corporate Gradient Banner */}
                    <div className="corporate-banner">
                      <div className="banner-content">
                        <div className="banner-text">
                          <span className="banner-title">Excellence in Motion</span>
                          <span className="banner-subtitle">Delivering your success, every day</span>
                        </div>
                        <div className="banner-gradient"></div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <Modal
        title="Nexusflow Success Story"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={720}
        centered
      >
        <div className="video-placeholder">
          <PlayCircleOutlined className="video-icon" />
          <p>Corporate Video Presentation</p>
        </div>
      </Modal>
    </>
  );
};

export default Hero;